// Import necessary React hooks and MUI icon
import React, { useState, useRef, useEffect } from 'react';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';

// Define the structure of a Notification object
interface Notification {
  id: string;
  title: string;
  message: string;
  acknowledged: boolean;
  seen: boolean;
  url: string;
}

// Define the Notifications component
const Notifications = () => {
  // State to store the list of notifications
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // State to control whether the notification box is open or closed
  const [isOpen, setIsOpen] = useState(false);
  // State to track the number of unread notifications
  const [unreadCount, setUnreadCount] = useState(0);
  // Refs to access DOM elements
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Update unreadCount whenever notifications change
  useEffect(() => {
    const count = notifications.reduce((acc, notification) => {
      return !notification.seen ? acc + 1 : acc;
    }, 0);
    setUnreadCount(count);
  }, [notifications]);

  // Function to toggle the notification box and mark notifications as seen
  const toggleNotify = () => {
    // If we're opening the notifications, mark them as seen
    if (!isOpen) {
      fetch('/notifications/mark_seen', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((response) => response.json())
        .then(() => {
          setNotifications(notifications.map(notification => ({
            ...notification,
            seen: true
          })));
        })
        .catch((error) => {
          console.error('Error marking notifications as seen:', error);
        });
    }

    setUnreadCount(0);  // Explicitly set unread count to 0
    setIsOpen((prev) => !prev);
  };

  // Effect to handle clicking outside the notification box
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close the notification box if clicked outside
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Add event listener for mousedown
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  // Effect to handle the animation of opening/closing the notification box
  useEffect(() => {
    if (buttonRef.current) {
      if (isOpen) {
        // Open the notification box with auto height
        buttonRef.current.style.height = 'auto';
        const height = buttonRef.current.scrollHeight;
        buttonRef.current.style.height = `${height}px`;
        buttonRef.current.style.opacity = '1';
        buttonRef.current.style.visibility = 'visible';
        buttonRef.current.style.pointerEvents = 'auto';
      } else {
        // Close the notification box
        buttonRef.current.style.height = '0px';
        buttonRef.current.style.opacity = '0';
        buttonRef.current.style.visibility = 'hidden';
        buttonRef.current.style.pointerEvents = 'none';
      }
    }
  }, [isOpen, notifications]);

  // Effect to fetch notifications from the API
  useEffect(() => {
    fetch('/notifications/', {
      method: 'GET',
    })
      .then((response) => response.json())
      .then((data) => {
        // Update the notifications state with fetched data
        setNotifications(data);
      })
      .catch((error) => {
        console.error('Error fetching notifications:', error);
      });
  }, []);

  // Function to truncate long strings
  const truncateString = (str: string, num: number) => {
    return str?.length > num ? str.slice(0, num) + '...' : str;
  };

  // Function to handle clicking on a notification
  const handleNotifyClick = (id: string, url: string) => {
    const encodedUrl = encodeURIComponent(url);
    window.location.href = `/notifications/${id}/acknowledge?redirect=${encodedUrl}`;
  };

  // Function to handle keyboard events for accessibility
  const handleKeyDown = (e: React.KeyboardEvent, id: string, url: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNotifyClick(id, url);
    }
  };

  // Render the Notifications component
  return (
    <div style={{ height: '60px', position: 'relative' }} ref={containerRef}>
      {/* Notification icon and badge */}
      <div title="View Notifications" onClick={toggleNotify} className='icon_div'>
        <NotificationsNoneOutlinedIcon className="mui_icon" />
        <div className='icon_notifier'>
          { unreadCount > 0 && !isOpen && (
            <span className="noti_icon_span">
              {unreadCount >= 9 ? ("9+") : (unreadCount)}
            </span>
          )}
        </div>
      </div>

      {/* Notification box */}
      <div className='notify_box' ref={buttonRef}>
        {/* Notification header */}
        <div className='not_tit_el'>
          <h2>Notifications</h2> <span>{notifications.length}</span>
        </div>
        {/* List of notifications (up to 3) */}
        {notifications.slice(0, 3).map((notification, index) => (
          <div
            onKeyDown={(e) => handleKeyDown(e, notification.id, notification.url)}
            role="button"
            tabIndex={0}
            key={index}
            className={`notify_item ${!notification.acknowledged ? "acknowledged" : " " }`}
            onClick={() => handleNotifyClick(notification.id, notification.url)}
          >
            <h4 className='notify_title' style={{color: "rgb(133, 2, 122)", paddingTop: "1px"}}>{notification.title}</h4>
            <p className='not_m'>{truncateString(notification.message, 50)}</p>
          </div>
        ))}

        {/* View all notifications button */}
        <div className='view_btn'>
          <a href= '/my_notifications'>
            <h3>VIEW ALL</h3>
          </a>
        </div>
      </div>
    </div>
  );
};

// Export the Notifications component
export default Notifications;
